import {
	CommonModule,
	isPlatformBrowser,
	isPlatformServer,
} from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	ElementRef,
	inject,
	Input,
	OnInit,
	PLATFORM_ID,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { AngularSvgIconModule } from 'angular-svg-icon';
import { MarkdownModule } from 'ngx-markdown';
import { register, SwiperContainer } from 'swiper/element/bundle';
import { SwiperOptions } from 'swiper/types';

import { ImageComponent } from '@valk-nx/components/ui-image/src/lib/image.component';
import { Sizes } from '@valk-nx/components/ui-image/src/lib/image.interface';
import { LinkComponent } from '@valk-nx/components/ui-link/src/lib/link';
import { VideoComponent } from '@valk-nx/components/ui-video/src/lib/video';
import { LazyImageLoadingModule } from '@valk-nx/core/lib/directives/lazy-image/lazy-image.module';
import { PreloadService } from '@valk-nx/core/lib/pipes/image-query/image-preload.service';
import { ImageQueryModule } from '@valk-nx/core/lib/pipes/image-query/image-query.module';
import { ImageQueryService } from '@valk-nx/core/lib/pipes/image-query/image-query.service';

import { CarouselItem } from './carousel.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	selector: 'vp-carousel',
	styleUrls: ['./carousel.scss'],
	templateUrl: './carousel.html',
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		AngularSvgIconModule,
		CommonModule,
		ImageQueryModule,
		LazyImageLoadingModule,
		LinkComponent,
		ImageComponent,
		MarkdownModule,
		RouterModule,
		VideoComponent,
	],
})
export class CarouselComponent implements OnInit, AfterViewInit {
	private readonly imageQuery = inject<ImageQueryService>(ImageQueryService);
	readonly platformId = inject(PLATFORM_ID);
	private readonly preloadService = inject(PreloadService);

	@Input() className = '';
	@Input() config: Partial<SwiperOptions> = {};
	@Input({ required: true }) items: CarouselItem[] = [];
	@Input() size: 'small' | 'large' = 'large';
	@Input() useEagerLoading = true;

	@ViewChild('swiper') swiperRef: ElementRef | undefined;

	swiper: SwiperContainer | null = null;
	swiperConfig: SwiperOptions = {
		loop: typeof window !== 'undefined' || this.items.length > 1,
		preventClicks: false,
		spaceBetween: 12,
		speed: 500,
	};

	imagesLoaded: boolean[] = [];
	isSSRMode = false;
	isCustomImages: boolean[] = [];
	sizes = Sizes;

	constructor() {
		register();
	}

	ngOnInit() {
		const preloadUrl = this.getPreloadUrl(
			this.items[0].image?.src || '',
			this.size,
		);
		this.preloadService.preloadImage(preloadUrl);
	}

	ngAfterViewInit() {
		this.imagesLoaded = new Array(this.items.length).fill(false);
		this.isCustomImages = new Array(this.items.length).fill(true);
		this.isSSRMode = isPlatformServer(this.platformId);

		this.swiperConfig = {
			...this.swiperConfig,
			...this.config,
			keyboard: { enabled: true, onlyInViewport: true },
			navigation: false,
		};

		this.swiper = this.swiperRef?.nativeElement;

		if (this.swiper && isPlatformBrowser(this.platformId)) {
			Object.assign(this.swiper, this.swiperConfig);
			this.swiper.initialize();
		}
	}

	onImageLoad(index: number) {
		this.imagesLoaded[index] = true;
	}

	slideNext() {
		this.swiper?.swiper.slideNext(this.swiperConfig.speed);
	}

	slidePrev() {
		this.swiper?.swiper.slidePrev(this.swiperConfig.speed);
	}

	setImageFallback(index: number) {
		this.isCustomImages[index] = false;

		const image = this.items[index].image
			? {
					image: {
						...this.items[index].image,
						src: '/assets/images/application-icons/icon-transparent-96x96.png',
					},
				}
			: null;

		this.items[index] = {
			...this.items[index],
			...image,
		};
	}

	getPreloadUrl(src: string, size: 'small' | 'large'): string {
		const targetWidth = 800;
		const targetHeight = size === 'large' ? 640 : 320;
		return this.imageQuery.transform(src, {
			width: targetWidth,
			height: targetHeight,
		});
	}

	getSrcSet(src: string, size: 'small' | 'large'): string {
		const config = [
			{ width: 480, height: size === 'large' ? 544 : 272 },
			{ width: 800, height: size === 'large' ? 640 : 320 },
			{ width: 1200, height: size === 'large' ? 640 : 320 },
			{ width: 1600, height: size === 'large' ? 640 : 320 },
			{ width: 2000, height: size === 'large' ? 640 : 320 },
		];

		return config
			.map(
				({ width, height }) =>
					`${this.imageQuery.transform(src, { width, height })} ${width}w`,
			)
			.join(', ');
	}

	getSizes(size: 'small' | 'large'): string {
		return size === 'large'
			? `(max-width: 640px) 480px, (max-width: 768px) 800px, (max-width: 1024px) 1200px, (max-width: 1280px) 1600px, 2000px`
			: `(max-width: 640px) 480px, (max-width: 768px) 800px, (max-width: 1024px) 1200px, 1600px`;
	}
}
